import { ChevronDown, LogOut, Settings, User as UserIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import type { RootState } from "../../app/store";
import { isReadOnlyPreviewAllowed } from "../../config/env";
import { useLogoutMutation } from "../../features/auth/auth.api";
import { logout, setMockAccessPreview } from "../../features/auth/auth.slice";
import type { MockAccessPreviewLevel } from "../../features/auth/auth.slice";
import {
	getChargesUnitDetail,
	getPropertyById,
	getRevenueUnitDetail,
	getServicePeriodById,
} from "../../features/financialManagement/financialManagement.mock";

const ACCESS_PREVIEW_OPTIONS: {
	value: MockAccessPreviewLevel;
	label: string;
}[] = [
	{ value: "READ", label: "Read" },
	{ value: "READ_WRITE", label: "Read & Write" },
	{ value: "APPROVE", label: "Approver" },
];

function getInitials(input?: string): string {
	if (!input) return "AD";
	const cleaned = input.trim();
	if (!cleaned) return "AD";
	return cleaned
		.split(/\s+/)
		.filter(Boolean)
		.map((n) => n[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

export const Header = () => {
	const user = useSelector((state: RootState) => state.auth.user);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [logoutMutation] = useLogoutMutation();
	const [profileMenuOpen, setProfileMenuOpen] = useState(false);
	const mockAccessPreview = useSelector(
		(state: RootState) => state.auth.mockAccessPreview,
	);
	const showAccessPreviewToggle = isReadOnlyPreviewAllowed();
	const menuRef = useRef<HTMLDivElement | null>(null);

	const displayName = user?.name ?? user?.email ?? "—";

	const moduleTitle = useMemo(() => {
		const path = location.pathname;
		if (path === "/dashboard") return "Dashboard";
		if (path.startsWith("/users")) return "PHO User Management";
		if (path.startsWith("/properties")) return "Property Management";
		if (path.startsWith("/property-marketing")) return "Property Marketing";
		if (path.startsWith("/events")) return "Event Management";
		if (path.startsWith("/cabana-booking")) return "Cabana Management";
		if (path.startsWith("/restaurant-booking")) return "F&B Management";
		if (path.startsWith("/approvals")) return "Beneficiary Approvals";
		if (path.startsWith("/roles-permissions")) return "Roles & Permissions";
		if (path.startsWith("/sub-admin")) return "Kerzner Sub Admin Management";
		if (path.startsWith("/app-update")) return "App Update";
		if (path.startsWith("/static-content")) return "Static Content";
		if (path.startsWith("/revenue-management")) return "Revenue Management";
		if (path.startsWith("/charges-management")) return "Charges Management";
		if (path.startsWith("/settings")) return "Settings";
		if (path.startsWith("/profile")) return "Profile";
		return "Dashboard";
	}, [location.pathname]);

	const moduleSubtitle = useMemo(() => {
		const path = location.pathname;
		if (path.startsWith("/users")) {
			return "Manage homeowners and their family members across all properties";
		}
		if (path.startsWith("/properties")) {
			return "Manage all Kerzner properties across the portfolio";
		}
		if (path.startsWith("/property-marketing")) {
			return "Manage marketing campaigns and promotions across properties";
		}
		if (path.startsWith("/events")) {
			return "Create and manage events for all your residents";
		}
		if (path.startsWith("/approvals")) {
			return "Review beneficiary request";
		}
		if (path.startsWith("/roles-permissions")) {
			return "Manage roles and access levels";
		}
		if (path.startsWith("/sub-admin")) {
			return "Manage sub admin users and their property access";
		}
		if (path.startsWith("/cabana-booking")) {
			if (path === "/cabana-booking" || path === "/cabana-booking/") {
				return "Manage cabanas and poolside venues across properties";
			}
			if (
				path === "/cabana-booking/create" ||
				/\/cabana-booking\/.+\/edit$/.test(path)
			) {
				return "Manage cabanas and poolside venues across properties";
			}
			return "View cabana details";
		}
		if (path.startsWith("/restaurant-booking")) {
			if (path === "/restaurant-booking" || path === "/restaurant-booking/") {
				return "View restaurant details";
			}
			if (
				path === "/restaurant-booking/create" ||
				/\/restaurant-booking\/.+\/edit$/.test(path)
			) {
				return "Manage restaurant listings across properties";
			}
			return "View restaurant details";
		}
		if (path.startsWith("/app-update")) {
			return "Manage app update notifications for iOS and Android.";
		}
		if (path.startsWith("/static-content")) {
			if (path === "/static-content" || path === "/static-content/") {
				return "Manage platform-wide documents shown to users";
			}
			const slug = path.split("/")[2] ?? "";
			const labels: Record<string, string> = {
				"terms-and-conditions": "Terms & Conditions",
				"privacy-policy": "Privacy Policy",
				"my-benefits": "My Benefits",
				"about-kerzner": "About Kerzner",
			};
			return labels[slug] ?? "Edit document";
		}
		if (path.startsWith("/revenue-management")) {
			if (path === "/revenue-management" || path === "/revenue-management/") {
				return "Manage rental revenue and owner payout statements across properties";
			}
			const parts = path.split("/").filter(Boolean);
			const propertyId = parts[2];
			const periodId = parts[4];
			const unitId = parts[6];
			const module = "revenue" as const;
			const property = propertyId
				? getPropertyById(propertyId, module)
				: undefined;
			const period = periodId ? getServicePeriodById(periodId) : undefined;
			const unit = unitId ? getRevenueUnitDetail(unitId) : undefined;
			if (unit && property && period) {
				return `${property.name} · ${unit.unitCode} · Statement ${unit.statementId}`;
			}
			if (property && period) {
				return `${property.name} · ${period.periodLabel}`;
			}
			if (property) {
				return `Service periods · ${property.name}`;
			}
			return "Manage rental revenue and owner payout statements across properties";
		}
		if (path.startsWith("/charges-management")) {
			if (path === "/charges-management" || path === "/charges-management/") {
				return "Manage HOA charges, service periods and homeowner payments";
			}
			const parts = path.split("/").filter(Boolean);
			const propertyId = parts[2];
			const periodId = parts[4];
			const unitId = parts[6];
			const module = "charges" as const;
			const property = propertyId
				? getPropertyById(propertyId, module)
				: undefined;
			const period = periodId ? getServicePeriodById(periodId) : undefined;
			const unit = unitId ? getChargesUnitDetail(unitId) : undefined;
			if (unit && property && period) {
				return `${property.name} · ${unit.unitCode} · Invoice ${unit.invoiceNumber}`;
			}
			if (property && period) {
				return `${property.name} · ${period.periodLabel}`;
			}
			if (property) {
				return `Service periods · ${property.name}`;
			}
			return "Manage HOA charges, service periods and homeowner payments";
		}
		return "";
	}, [location.pathname]);

	useEffect(() => {
		function onDocClick(e: MouseEvent) {
			if (!menuRef.current) return;
			if (!menuRef.current.contains(e.target as Node)) {
				setProfileMenuOpen(false);
			}
		}
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	const handleLogout = async () => {
		try {
			await logoutMutation().unwrap();
		} catch (_error) {
			// Ignore API failure, still clear local state.
		}
		dispatch(logout());
		navigate("/login", { replace: true });
	};

	return (
		<header className="min-h-[var(--height-header)] border-b border-border bg-bg-card px-6 py-3">
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0">
					<h1 className="truncate text-xl font-bold leading-7 text-primary">
						{moduleTitle}
					</h1>
					{moduleSubtitle && (
						<p className="mt-1 text-base leading-6 text-primary-muted">
							{moduleSubtitle}
						</p>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-3">
					{showAccessPreviewToggle ? (
						<label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-bg-input px-3 py-2 text-sm text-input-text">
							<span className="whitespace-nowrap">Preview access</span>
							<select
								value={mockAccessPreview}
								onChange={(e) =>
									dispatch(
										setMockAccessPreview(
											e.target.value as MockAccessPreviewLevel,
										),
									)
								}
								aria-label="Preview access level"
								className="h-8 min-w-[140px] rounded-md border border-border bg-bg-card px-2 text-sm text-input-text"
							>
								{ACCESS_PREVIEW_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</label>
					) : null}
					<div className="relative" ref={menuRef}>
						<button
							type="button"
							onClick={() => setProfileMenuOpen((v) => !v)}
							className="inline-flex items-center gap-2 rounded-xl border border-border bg-bg-card px-2.5 py-1.5 text-left hover:bg-bg-input"
						>
							<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent text-sm text-white">
								{getInitials(user?.name ?? user?.email)}
							</div>
							<div className="max-w-[160px] truncate text-sm font-medium text-primary">
								{displayName}
							</div>
							<ChevronDown className="h-4 w-4 text-primary-muted" />
						</button>
						{profileMenuOpen && (
							<div className="absolute right-0 z-30 mt-2 w-48 overflow-hidden rounded-xl border border-border bg-bg-card shadow-lg">
								<button
									type="button"
									onClick={() => {
										setProfileMenuOpen(false);
										navigate("/profile");
									}}
									className="flex w-full items-center gap-2 px-3 py-2 text-sm text-input-text hover:bg-bg-input"
								>
									<UserIcon className="h-4 w-4" />
									Profile
								</button>
								<button
									type="button"
									onClick={() => {
										setProfileMenuOpen(false);
										navigate("/settings");
									}}
									className="flex w-full items-center gap-2 px-3 py-2 text-sm text-input-text hover:bg-bg-input"
								>
									<Settings className="h-4 w-4" />
									Settings
								</button>
								<button
									type="button"
									onClick={handleLogout}
									className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-sm text-status-invite-expired hover:bg-red-50"
								>
									<LogOut className="h-4 w-4" />
									Logout
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</header>
	);
};
